// src/components/SpendingLimitNotice.js
// One purchase-limit or card line with its buttons (STEP3_5A_TIERS_DESIGN.md
// 3.7 and 5.14; release R3, dark in 'cap' mode). Used by the Super Crit
// panels, StreamRumble and TsunamiMeter.
//
// Renders nothing unless the server sent 'limits'-mode data: pass either
//  - `limit` (a setup's limit object, or a refusal's details.limit): the line
//    is computed here with a minute clock, so a cooldown counts down, and
//    `onExpire` runs once when more room frees up (the caller re-fetches its
//    setup or clears a refusal); or
//  - `line` (from cardLine / captureOutcomeLine): a fixed line.
//
// Buttons follow the line: "Verify your ID and age" (L11) opens
// IdVerification; "Contact support" (L11s) opens a mailto; nothing for
// in_review (L11r); "Use another card" when the line offers it and the
// caller passes onUseAnotherCard. A caller inside a modal passes onVerify (or
// onBeforeNavigate) to close the modal first: a React Native Web modal stays
// on screen over the next route otherwise.

import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { limitLines, VERIFY_ROUTE } from '../utils/spendingLimit';

const COLORS = {
  GOLD: '#FCD000',
  INFO: '#c9c9c9',
  WARN: '#f5c542',
  ERROR: '#e05555',
};

// Re-renders once a minute while a cooldown is shown, and right when it ends.
function useCooldownClock(deadlineMs) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (typeof deadlineMs !== 'number' || !Number.isFinite(deadlineMs)) return undefined;
    const left = deadlineMs - Date.now();
    const wait = left > 0 ? Math.min(60000, left + 250) : 60000;
    const t = setTimeout(() => setNowMs(Date.now()), Math.max(1000, wait));
    return () => clearTimeout(t);
  }, [deadlineMs, nowMs]);
  return nowMs;
}

function useOptionalNavigation() {
  try {
    return useNavigation();
  } catch (_) {
    return null; // outside a navigator: the verify button calls onVerify only
  }
}

export default function SpendingLimitNotice({
  limit = null,
  line: fixedLine = null,
  surface = 'tip',
  amountCents = null,
  tone = null,
  onExpire,
  onVerify,
  onBeforeNavigate,
  onUseAnotherCard,
  disabled = false,
  style,
}) {
  const navigation = useOptionalNavigation();
  const preview = limit ? limitLines(limit, { surface, amountCents }) : null;
  const nowMs = useCooldownClock(preview && preview.atLimit ? preview.nextFreeAtMs : null);
  const line = limit ? limitLines(limit, { surface, amountCents, nowMs }) : fixedLine;

  // onExpire once per cooldown end (a re-fetch that returns the same time
  // never loops).
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const firedForRef = useRef(null);
  const deadline = line && line.atLimit ? line.nextFreeAtMs : null;
  useEffect(() => {
    if (typeof deadline !== 'number' || nowMs < deadline || firedForRef.current === deadline) return;
    firedForRef.current = deadline;
    if (onExpireRef.current) onExpireRef.current();
  }, [deadline, nowMs]);

  if (!line || !line.text) return null;

  const color = tone === 'error' ? COLORS.ERROR
    : tone === 'info' ? COLORS.INFO
      : (line.atLimit || line.id === 'L2' ? COLORS.WARN : COLORS.INFO);

  const goVerify = () => {
    if (onVerify) { onVerify(); return; }
    if (onBeforeNavigate) onBeforeNavigate();
    if (navigation) navigation.navigate(VERIFY_ROUTE);
  };
  const goSupport = (href) => {
    Linking.openURL(href).catch(() => {});
  };

  const button = line.button || null;
  const showRetry = !!(line.retryLabel && onUseAnotherCard);
  const showVerify = !!(button && button.kind === 'verify' && (onVerify || navigation));
  const showSupport = !!(button && button.kind === 'support');

  return (
    <View style={[styles.box, style]}>
      <Text style={[styles.text, { color }]}>{line.text}</Text>
      {showRetry || showVerify || showSupport ? (
        <View style={styles.buttons}>
          {showRetry ? (
            <TouchableOpacity
              style={[styles.btnOutline, disabled && styles.btnDisabled]}
              onPress={onUseAnotherCard}
              disabled={disabled}
              accessibilityRole="button"
            >
              <Text style={styles.btnOutlineText}>{line.retryLabel}</Text>
            </TouchableOpacity>
          ) : null}
          {showVerify ? (
            <TouchableOpacity
              style={[styles.btnGold, disabled && styles.btnDisabled]}
              onPress={goVerify}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityLabel={button.accessibilityLabel}
            >
              <Text style={styles.btnGoldText}>{button.label}</Text>
            </TouchableOpacity>
          ) : null}
          {showSupport ? (
            <TouchableOpacity
              style={[styles.btnOutline, disabled && styles.btnDisabled]}
              onPress={() => goSupport(button.href)}
              disabled={disabled}
              accessibilityRole="link"
            >
              <Text style={styles.btnOutlineText}>{button.label}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  box: { marginTop: 10 },
  text: { fontSize: 12, lineHeight: 17, textAlign: 'center' },
  buttons: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 8 },
  btnGold: {
    backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 14,
  },
  btnGoldText: { color: '#0b0b0b', fontSize: 12.5, fontWeight: '800' },
  btnOutline: {
    borderColor: COLORS.GOLD, borderWidth: 1, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14,
  },
  btnOutlineText: { color: COLORS.GOLD, fontSize: 12.5, fontWeight: '700' },
  btnDisabled: { opacity: 0.5 },
});
